import { useState, useCallback, useEffect, useMemo } from 'react'
import { Box, Button } from '@mui/material'
import {
  DataGrid,
  type GridColDef,
  GridActionsCellItem,
  GridRowModes,
  GridRowModesModel,
  GridEventListener,
  GridRowEditStopReasons,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { usePOTracking } from '@/hooks/usePOTracking'
import type { DatabaseInvoice, ColumnDefinition } from '@/types/database'
import type { PurchaseOrder } from '@/types/po'

const MAX_PO_DESCRIPTION_LENGTH = 10

const truncateDescription = (description: string | null): string => {
  if (!description) return ''
  const trimmed = description.trim()
  if (!trimmed) return ''
  return trimmed.length > MAX_PO_DESCRIPTION_LENGTH
    ? `${trimmed.slice(0, MAX_PO_DESCRIPTION_LENGTH)}...`
    : trimmed
}

const formatPOLabel = (po: PurchaseOrder): string => {
  const truncated = truncateDescription(po.description)
  return truncated ? `${po.poNumber} - ${truncated}` : po.poNumber
}

const getInvoiceYear = (dateStr: string | null): number | null => {
  if (!dateStr) return null
  const trimmed = dateStr.trim()
  if (!trimmed) return null

  const isoMatch = /^(\d{4})-\d{2}-\d{2}/.exec(trimmed)
  if (isoMatch) return Number(isoMatch[1])

  const deMatch = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed)
  if (deMatch) return Number(deMatch[3])

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getFullYear()
}

interface DatabaseTableProps {
  invoices: DatabaseInvoice[]
  columns: ColumnDefinition[]
  pageSize: number
  totalCount: number
  page: number
  highlightId?: string | null
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onEdit: (id: string, data: Partial<DatabaseInvoice>) => Promise<void> | void
  onDelete: (id: string) => Promise<void> | void
  onCreate: (data: Partial<DatabaseInvoice>) => Promise<void> | void
}

export function DatabaseTable({
  invoices,
  pageSize,
  totalCount,
  page,
  highlightId,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onCreate,
}: DatabaseTableProps) {
  const apiBaseUrl = (import.meta.env.VITE_API_URL || '/v1').replace(/\/$/, '')
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [rows, setRows] = useState<DatabaseInvoice[]>(invoices)

  // PO Tracking - load all POs for per-row year filtering
  const { usePOs } = usePOTracking()
  const { data: pos = [] } = usePOs()

  // Update rows when invoices change
  useEffect(() => {
    setRows(invoices)
  }, [invoices])

  // Handle row edit stop
  const handleRowEditStop: GridEventListener<'rowEditStop'> = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }, [])

  // Handle edit click
  const handleEditClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }))
  }, [])

  // Handle save click
  const handleSaveClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }))
  }, [])

  // Handle delete click
  const handleDeleteClick = useCallback((id: string) => () => {
    void onDelete(id)
  }, [onDelete])

  // Handle cancel click
  const handleCancelClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }))
  }, [])

  // Process row update
  const processRowUpdate = useCallback(async (newRow: DatabaseInvoice, oldRow: DatabaseInvoice) => {
    const updatedRow = { ...newRow }
    const isNewRow = oldRow.id.startsWith('new-')
    setRows((prev) => prev.map((row) => (row.id === oldRow.id ? updatedRow : row)))

    try {
      if (isNewRow) {
        await onCreate(updatedRow)
      } else {
        await onEdit(updatedRow.id, updatedRow)
      }
      return updatedRow
    } catch (error) {
      setRows((prev) => {
        if (isNewRow) {
          return prev.filter((row) => row.id !== updatedRow.id)
        }
        return prev.map((row) => (row.id === updatedRow.id ? oldRow : row))
      })
      throw error instanceof Error ? error : new Error('Failed to save row')
    }
  }, [onCreate, onEdit])

  // Handle row modes model change
  const handleRowModesModelChange = useCallback((newModel: GridRowModesModel) => {
    setRowModesModel(newModel)
  }, [])

  // Handle add new row
  const handleAddNew = useCallback(() => {
    const newId = `new-${Date.now()}`
    const newRow: DatabaseInvoice = {
      id: newId,
      supplierName: '',
      dateInvoice: null,
      brutto: null,
      netto: null,
      vatAmount: null,
      taxFreeAmount: null,
      taxableNetAmount: null,
      rechnungsnummer: '',
      comment: '',
      currency: null,
      projectName: null,
      recipient: null,
      ustId: null,
      confidence: null,
      source: 'dashboard',
      customFields: {},
      vatBreakdown: null,
      taxFreeBreakdown: null,
      sourceDocumentId: null,
      poId: null,
      originalCurrency: null,
      originalBrutto: null,
      originalNetto: null,
      originalVatAmount: null,
      originalTaxFreeAmount: null,
      originalTaxableNetAmount: null,
      fxRate: null,
      fxRateDate: null,
      fxRateSource: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setRows((prev) => [newRow, ...prev])
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'supplierName' } }))
  }, [])

  // Base columns
  const baseColumns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id.toString())}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id.toString())}
              color="inherit"
            />,
          ]
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id.toString())}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id.toString())}
            color="inherit"
          />,
        ]
      },
    },
    { field: 'supplierName', headerName: 'Supplier', width: 180, editable: true },
    { field: 'dateInvoice', headerName: 'Date', width: 120, editable: true, type: 'string' },
    {
      field: 'brutto',
      headerName: 'Gross',
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'netto',
      headerName: 'Net',
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'vatAmount',
      headerName: 'VAT',
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'taxFreeAmount',
      headerName: 'Tax-Free',
      width: 110,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'taxableNetAmount',
      headerName: 'Taxable Net',
      width: 120,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    { field: 'rechnungsnummer', headerName: 'Invoice #', width: 140, editable: true },
    { field: 'comment', headerName: 'Comment', width: 200, editable: true },
    { field: 'currency', headerName: 'Currency', width: 80, editable: true },
    { field: 'projectName', headerName: 'Project', width: 150, editable: true },
    { field: 'recipient', headerName: 'Recipient', width: 180, editable: true },
    { field: 'ustId', headerName: 'VAT ID', width: 120, editable: true },
    { field: 'confidence', headerName: 'Confidence', width: 100, editable: true },
    { field: 'source', headerName: 'Origin', width: 100, editable: false },
    {
      field: 'sourceDocumentId',
      headerName: 'Source',
      width: 140,
      editable: false,
      renderCell: (params) => {
        if (!params.value) return '-'
        const href = `${apiBaseUrl}/documents/${params.value}/file`
        return (
          <a href={href} target="_blank" rel="noreferrer">
            Download
          </a>
        )
      },
    },
    {
      field: 'poId',
      headerName: 'PO Number',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: useMemo(() => {
        return (params) => {
          const row = params.row as DatabaseInvoice
          const options = [{ value: null as string | null, label: 'Unassigned' }]
          if (pos.length === 0) return options

          const invoiceYear = getInvoiceYear(row.dateInvoice)
          const filtered = invoiceYear ? pos.filter((po) => po.year === invoiceYear) : pos

          filtered.forEach((po: PurchaseOrder) => {
            options.push({ value: po.id, label: formatPOLabel(po) })
          })

          if (row.poId) {
            const selected = pos.find((po) => po.id === row.poId)
            const alreadyIncluded = selected && filtered.some((po) => po.id === selected.id)
            if (selected && !alreadyIncluded) {
              options.push({ value: selected.id, label: formatPOLabel(selected) })
            }
          }

          return options
        }
      }, [pos]),
      valueFormatter: (value: string | null) => {
        const po = pos.find((p: PurchaseOrder) => p.id === value)
        return po ? formatPOLabel(po) : 'Unassigned'
      },
    },
    {
      field: 'original',
      headerName: 'Original',
      width: 180,
      editable: false,
      renderCell: (params) => {
        const row = params.row as DatabaseInvoice
        if (!row.originalCurrency || row.originalNetto == null) return '-'
        const value = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(row.originalNetto)
        return `${value} ${row.originalCurrency} (FX ${row.fxRateDate || '-'})`
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      editable: false,
      valueFormatter: (value) => {
        if (!value) return ''
        return new Date(value).toLocaleString('de-DE')
      },
    },
  ]

  const Toolbar = useCallback(() => (
    <GridToolbarContainer>
      <GridToolbarQuickFilter />
      <Box sx={{ flexGrow: 1 }} />
      <Button startIcon={<AddIcon />} onClick={handleAddNew} size="small">
        Add Invoice
      </Button>
    </GridToolbarContainer>
  ), [handleAddNew])

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={baseColumns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        getRowClassName={(params) => (highlightId && params.id === highlightId ? 'invoice-highlight' : '')}
        pageSizeOptions={[25, 50, 100]}
        pagination
        paginationModel={{ page: page - 1, pageSize }}
        onPaginationModelChange={(model) => {
          onPageChange(model.page + 1)
          onPageSizeChange(model.pageSize)
        }}
        rowCount={totalCount}
        paginationMode="server"
        disableRowSelectionOnClick
        slots={{
          toolbar: Toolbar,
        }}
        sx={{
          '& .invoice-highlight': {
            backgroundColor: 'rgba(255, 235, 133, 0.4)',
            '&:hover': {
              backgroundColor: 'rgba(255, 235, 133, 0.6)',
            },
          },
        }}
      />
    </Box>
  )
}
